import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
// import classnames from "classnames";

import dotProp from "dot-prop-immutable"

import { useFirebase } from "@meddbriefer/mdb-firebase"

import { getScenarioByID, intvVariant2JSON } from "@meddbriefer/scenario-data"
import {
    PROTOCOL_RELATIONSHIPS as PR, 
    // SELECTION_TYPES as ST 
} from "@meddbriefer/scenario-data/constants"
import { groupByKey } from "@meddbriefer/scenario-data/utils"


import ScenarioEditorLayout from "./ScenarioEditorLayout"
import {useScenarioEditor} from "./ScenarioEditorContext"

import IntvVariantPrompts from "./IntvVariantPrompts";

const getLabel = (obj) => {
    let retVal = "Unable to find label for non-existant object"
    if (obj) {
        if ("abbrLabel" in obj && "" !== obj.abbrLabel) {
            retVal = obj.abbrLabel;
        } else {
            retVal = obj.label;
        }
    }
    return retVal   
}

export default function InterventionAnswers() {
    const {db} = useFirebase()
    const {scenarioId} = useParams()
    const {scenario, scenarioType, updateScenarioFld, saveScenario} = useScenarioEditor()
    const [intvs, setIntvs] = useState(null)
    const [intvVariantContexts, setIntvVariantContexts] = useState(null)
    const [state, setState] = useState()
    const [readyToSave, setReadyToSave] = useState(false)

    const saveHandler = async (event) => {
        // console.log("save handler clicked", state)
        updateScenarioFld("interventionVariantAnswers", state)
        const scen = await getScenarioByID(db, scenarioId)
        scen.interventionVariantAnswers = state
        await saveScenario(scen)
    }

    const getIntervention = (intvID) => intvs.find(rec => rec.id === intvID)
    const getIntvVariantPromptState = (intvID, variantJSON, promptID) => state[intvID][variantJSON][promptID]
    const setIntvVariantPromptState = (intvID, variantJSON, promptID, val) => {
        const fldPath = `${intvID}.${variantJSON}.${promptID}`
        // console.log("settingState: ", fldPath, val)
        setState(dotProp.set(state, fldPath, val))
    }

    useEffect(
        // on page load:
        // figure out the set of interventions by reviewing current problems, solutions and actions
        //  * only consider actions with protocol relationship of REQUIRED
        //  * then filter out interventions without prompts
        //  * then group them by intervention variant, resulting in intv variants mapped to the list of actions
        //    they are associated with, which is most likely one, but unfortunately we need to do this
        () => {
            if (!intvs) {
                // get set of all intervention IDs listed for REQUIRED actions
                const intvIDs = Array.from(new Set(scenario.actions.filter(act => [PR.REQ, PR.ALT, PR.OPT].includes(act.protocolRelationship))
                    .map(e => e.interventionID)))
                // console.log(intvIDs)
                // filter this down to only interventions which have prompts
                const intvIDsWithPrompts = intvIDs.filter(intvID => {
                    const intv = scenarioType.getIntervention(intvID)
                    return intv.prompts !== undefined && intv.prompts.length > 0
                })
                // console.log(intvIDsWithPrompts)
                setIntvs(intvIDsWithPrompts.map(intvID => scenarioType.getInterventionForm(intvID)))
            }
        }, [intvs, scenario.actions, scenarioType]
    )

    useEffect(
        () => {
        if (!!intvs && !intvVariantContexts) {
            const intvIDsWithPrompts = intvs.map(intv => intv.id)
            // console.log(intvIDsWithPrompts)
            // get all actions which refer to this filtered set of intervention IDs
            const relevantActions = scenario.actions.filter(act => intvIDsWithPrompts.includes(act.interventionID))
                .map(rec => Object.assign({}, rec, {
                    interventionVariant: intvVariant2JSON(rec.interventionVariant)
                }))
            // console.log(relevantActions)

            // group relevent actions by interventionID  intvID => action[]
            const actionsByIntvID = groupByKey(relevantActions, "interventionID")
            // console.log(actionsByIntvID)

            // create secondary index on interventionVariant  intvID => intvVarariant => action[]
            // const intvID2Variant2Actions = {}
            const ctxs = []
            Object.entries(actionsByIntvID).forEach(([intvID, actions]) => {
                const actionsByIntvVariant = groupByKey(actions, "interventionVariant")
                Object.entries(actionsByIntvVariant).forEach(([variantJSON, actions]) => {
                    const variant = JSON.parse(variantJSON)
                    let variantDesc = ""
                    Object.entries(variant).forEach(([promptID, ansID]) => {
                        const pt = scenarioType.getPrompt(promptID)
                        if (!pt) {
                            console.log(`prompt: ${promptID} not found`)
                        }
                        const promptLabel = getLabel(pt)
                        const ans = scenarioType.getAnswer(ansID)
                        if (!ans) {
                            console.log(`intv: ${intvID} prompt ${promptID}: answer: ${ansID} not found`)
                        }
                        const ansLabel = getLabel(ans)
                        variantDesc += ` ${promptLabel} ${ansLabel}`
                    })

                    // console.log(variantJSON, actions)
                    const contexts = actions.map(action => {
                        const prob = scenario.problems.find(rec => rec.id === action.problemID)
                        const sol = scenario.solutions.find(rec => rec.id === action.solutionID)
                        return `${prob.label} - ${sol.label}`
                    }).join(", ")
                    ctxs.push({
                        intvID,
                        variantJSON,
                        intvLabel: intvs.find(rec => rec.id === intvID).abbrLabel,
                        variantDesc,
                        contexts
                    })
                })
                // intvID2Variant2Actions[intvID] = actionsByIntvVariant
            })
            // console.log(intvID2Variant2Actions)
            // console.log(ctxs)

            setIntvVariantContexts(ctxs)
        }
        }, [scenarioId, intvs, intvVariantContexts, scenario.problems, scenario.solutions, scenario.actions, scenarioType]
    )

    // const getDefaultAnswer = (prompt) => (prompt.selectionType === ST.ONE) ? "" : []


    useEffect(
        // on page load, after the computing of actions:
        // create non-filled out forms and clobber with any answers already stored in the db

        () => {
            if (!!intvVariantContexts && !state) {
                const intvAnswers = scenario.interventionVariantAnswers
                const initState = {}
                intvVariantContexts.forEach(ctx => {
                    const {intvID, variantJSON} = ctx

                    if (! (intvID in initState)) {
                        initState[intvID] = {}
                    }
                    initState[intvID][variantJSON] = {}
                    const intv = intvs.find(intv => intv.id === intvID)
                    intv.prompts.forEach(prompt => {
                        if (intvAnswers[intvID] && intvAnswers[intvID][variantJSON] && intvAnswers[intvID][variantJSON][prompt.id]) {
                            let dbStoredAnswer = intvAnswers[intvID][variantJSON][prompt.id]
                            if (!Array.isArray(dbStoredAnswer)) {
                                //ensure all prompt answers are now arrays
                                dbStoredAnswer = [dbStoredAnswer]
                            }
                            initState[intvID][variantJSON][prompt.id] = dbStoredAnswer
                         } else {
                            // all answers are initialized to empty array, regardless of prompt selectionType value
                            initState[intvID][variantJSON][prompt.id] = []
                        }
                    })
                })
                setState(initState)
            }
        }, [state, scenario, intvs, intvVariantContexts]
    )

    useEffect(
        () => {
            // whenever state changes determine whether all fields are filled out, and thus
            // ready to save
            if (state) {
                // console.log("state changed", state)
                let filledOut = true
                Object.keys(state).forEach(intvID => {
                    Object.keys(state[intvID]).forEach(variant => {
                        Object.entries(state[intvID][variant]).forEach(([promptID, value]) => {
                            if (value === "" || ("object" === typeof value && value.length === 0)) {
                                filledOut = false
                            }
                        })
                    })
                })
                setReadyToSave(filledOut)
            }
        }, [state]
    )

    if (!state) {
        return (<div>loading...</div>)
    }
    return (
        <ScenarioEditorLayout
            label="Save Changes to Intervention Answers"
            haveChanges={readyToSave}
            saveHandler={saveHandler}
        >
            <div id="intv-answers" className="info-wrapper">
                {intvVariantContexts.map((ctx, i) =>
                    <div key={i} style={{marginBottom: "20px"}}>
                        <IntvVariantPrompts
                            intvGetter={getIntervention}
                            promptValueGetter={getIntvVariantPromptState}
                            promptValueSetter={setIntvVariantPromptState}
                            {...ctx}/>
                    </div>
                )}
            </div>
        </ScenarioEditorLayout>
    )
}
